import type { DeliverySource, Telemetry } from "./types.js";
import { Reservoir } from "./reservoir.js";

/** One delivered segment, as seen by the metrics layer. */
export interface DeliveryRecord {
  source: DeliverySource;
  elapsedMs: number;
  deadlineMs: number;
  bytes: number;
  fromPeer: string | null;
}

export interface LatencyStat {
  p50: number;
  p95: number;
  n: number;
}
export interface PeerRttStat {
  peerId: string;
  p50: number;
  p95: number;
  n: number;
}
export interface SeriesPoint {
  t: number; // seconds since metrics start
  offloadRatio: number;
  activePeers: number;
  p2pLatencyP50: number;
  httpLatencyP50: number;
}

export interface MetricsSnapshot {
  bytesP2P: number;
  bytesHttp: number;
  segmentsP2P: number;
  segmentsHttp: number;
  activePeers: number;
  offloadRatio: number;
  sources: Record<DeliverySource, { bytes: number; count: number }>;
  latency: { p2p: LatencyStat; http: LatencyStat };
  deadline: { total: number; missed: number; missRate: number };
  hedgeWinRate: number;
  peers: PeerRttStat[];
  series: SeriesPoint[];
}

const SERIES_CAP = 120; // ~2 min at 1 point/sec

/**
 * Rich engine metrics. Extends the flat counters the rest of the system already
 * reads (bytesP2P, segmentsHttp, …) with per-source breakdown, delivery-latency
 * and RTT quantiles, a deadline-miss rate (our rebuffer proxy), and a
 * time-series ring for charting. Implements Telemetry so it drops in wherever
 * the old TelemetryCounters was used.
 */
export class EngineMetrics implements Telemetry {
  // --- legacy flat counters (kept for back-compat with e2e/harness) ---
  bytesP2P = 0;
  bytesHttp = 0;
  segmentsP2P = 0;
  segmentsHttp = 0;
  hedgeHttpWins = 0;
  activePeers = 0;

  private readonly sources: Record<DeliverySource, { bytes: number; count: number }> = {
    p2p: { bytes: 0, count: 0 },
    http: { bytes: 0, count: 0 },
    "http-hedge-win": { bytes: 0, count: 0 },
    cache: { bytes: 0, count: 0 },
  };
  private readonly latP2P = new Reservoir();
  private readonly latHttp = new Reservoir();
  private readonly rtt = new Map<string, Reservoir>();
  private deliveries = 0;
  private deadlineMisses = 0;
  private readonly series: SeriesPoint[] = [];
  private startMs = now();

  offloadRatio(): number {
    const total = this.bytesP2P + this.bytesHttp;
    return total === 0 ? 0 : this.bytesP2P / total;
  }

  recordDelivery(d: DeliveryRecord): void {
    const s = this.sources[d.source];
    s.bytes += d.bytes;
    s.count++;

    if (d.source === "p2p") {
      this.bytesP2P += d.bytes;
      this.segmentsP2P++;
      this.latP2P.add(d.elapsedMs);
    } else if (d.source === "http" || d.source === "http-hedge-win") {
      this.bytesHttp += d.bytes;
      this.segmentsHttp++;
      this.latHttp.add(d.elapsedMs);
      if (d.source === "http-hedge-win") this.hedgeHttpWins++;
    }
    // cache deliveries cost no network — tracked in `sources` only.

    if (d.source !== "cache") {
      this.deliveries++;
      if (d.elapsedMs > d.deadlineMs) this.deadlineMisses++;
    }
  }

  recordRtt(peerId: string, ms: number): void {
    let r = this.rtt.get(peerId);
    if (!r) {
      r = new Reservoir(128);
      this.rtt.set(peerId, r);
    }
    r.add(ms);
  }

  dropPeer(peerId: string): void {
    this.rtt.delete(peerId);
  }

  /** Push one time-series point. Call ~1×/sec while active. */
  tick(): void {
    this.series.push({
      t: (now() - this.startMs) / 1000,
      offloadRatio: this.offloadRatio(),
      activePeers: this.activePeers,
      p2pLatencyP50: this.latP2P.percentile(0.5),
      httpLatencyP50: this.latHttp.percentile(0.5),
    });
    if (this.series.length > SERIES_CAP) this.series.shift();
  }

  snapshot(): MetricsSnapshot {
    const stat = (r: Reservoir): LatencyStat => ({
      p50: round(r.percentile(0.5)),
      p95: round(r.percentile(0.95)),
      n: r.size,
    });
    const peers: PeerRttStat[] = [...this.rtt.entries()].map(([peerId, r]) => ({
      peerId,
      p50: round(r.percentile(0.5)),
      p95: round(r.percentile(0.95)),
      n: r.size,
    }));
    const httpCount = this.sources.http.count + this.sources["http-hedge-win"].count;
    return {
      bytesP2P: this.bytesP2P,
      bytesHttp: this.bytesHttp,
      segmentsP2P: this.segmentsP2P,
      segmentsHttp: this.segmentsHttp,
      activePeers: this.activePeers,
      offloadRatio: this.offloadRatio(),
      // Shallow per-entry copy (leaves are numbers) — avoids a JSON deep-clone
      // on every snapshot (called on the dashboard poll AND the report timer).
      sources: {
        p2p: { ...this.sources.p2p },
        http: { ...this.sources.http },
        "http-hedge-win": { ...this.sources["http-hedge-win"] },
        cache: { ...this.sources.cache },
      },
      latency: { p2p: stat(this.latP2P), http: stat(this.latHttp) },
      deadline: {
        total: this.deliveries,
        missed: this.deadlineMisses,
        missRate: this.deliveries === 0 ? 0 : this.deadlineMisses / this.deliveries,
      },
      hedgeWinRate: httpCount === 0 ? 0 : this.sources["http-hedge-win"].count / httpCount,
      peers,
      series: [...this.series],
    };
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}
function round(n: number): number {
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : 0;
}
